import { defineComponent, h } from "vue";
import copiaClosedPr from "../../public/img/svg/copia-closed-pr.svg";
import copiaMergedPr from "../../public/img/svg/copia-merged-pr.svg";
import copiaOpenPr from "../../public/img/svg/copia-opened-pr.svg";
import octiconAlert from "../../public/img/svg/octicon-alert.svg";
import octiconArchive from "../../public/img/svg/octicon-archive.svg";
import octiconBlocked from "../../public/img/svg/octicon-blocked.svg";
import octiconCheckCircleFill from "../../public/img/svg/octicon-check-circle-fill.svg";
import octiconChevronDown from "../../public/img/svg/octicon-chevron-down.svg";
import octiconChevronRight from "../../public/img/svg/octicon-chevron-right.svg";
import octiconClock from "../../public/img/svg/octicon-clock.svg";
import octiconFile from "../../public/img/svg/octicon-file.svg";
import octiconGitMerge from "../../public/img/svg/octicon-git-merge.svg";
import octiconGitPullRequest from "../../public/img/svg/octicon-git-pull-request.svg";
import octiconIssueClosed from "../../public/img/svg/octicon-issue-closed.svg";
import octiconIssueOpened from "../../public/img/svg/octicon-issue-opened.svg";
import octiconLink from "../../public/img/svg/octicon-link.svg";
import octiconListUnordered from "../../public/img/svg/octicon-list-unordered.svg";
import octiconLock from "../../public/img/svg/octicon-lock.svg";
import octiconMeter from "../../public/img/svg/octicon-meter.svg";
import octiconMilestone from "../../public/img/svg/octicon-milestone.svg";
import octiconMirror from "../../public/img/svg/octicon-mirror.svg";
import octiconProject from "../../public/img/svg/octicon-project.svg";
import octiconRepoForked from "../../public/img/svg/octicon-repo-forked.svg";
import octiconRepoTemplate from "../../public/img/svg/octicon-repo-template.svg";
import octiconRepo from "../../public/img/svg/octicon-repo.svg";
import octiconSkip from "../../public/img/svg/octicon-skip.svg";
import octiconSync from "../../public/img/svg/octicon-sync.svg";
import octiconTrashcan from "../../public/img/svg/octicon-trashcan.svg";
import octiconXCircleFill from "../../public/img/svg/octicon-x-circle-fill.svg";
import octiconZoomIn from "../../public/img/svg/octicon-zoom-in.svg";
import octiconZoomOut from "../../public/img/svg/octicon-zoom-out.svg";
import { html, htmlRaw } from "./html.js";
import { parseDom } from "./utils.js";

export const svgs = {
  "octicon-alert": octiconAlert,
  "octicon-blocked": octiconBlocked,
  "octicon-check-circle-fill": octiconCheckCircleFill,
  "octicon-chevron-down": octiconChevronDown,
  "octicon-chevron-right": octiconChevronRight,
  "octicon-clock": octiconClock,
  "octicon-file": octiconFile,
  "octicon-git-merge": octiconGitMerge,
  "octicon-git-pull-request": octiconGitPullRequest,
  "octicon-issue-closed": octiconIssueClosed,
  "octicon-issue-opened": octiconIssueOpened,
  "octicon-link": octiconLink,
  "octicon-list-unordered": octiconListUnordered,
  "octicon-lock": octiconLock,
  "octicon-meter": octiconMeter,
  "octicon-milestone": octiconMilestone,
  "octicon-mirror": octiconMirror,
  "octicon-project": octiconProject,
  "octicon-repo": octiconRepo,
  "octicon-repo-forked": octiconRepoForked,
  "octicon-repo-template": octiconRepoTemplate,
  "octicon-skip": octiconSkip,
  "octicon-sync": octiconSync,
  "octicon-trashcan": octiconTrashcan,
  "octicon-zoom-in": octiconZoomIn,
  "octicon-zoom-out": octiconZoomOut,
  "octicon-archive": octiconArchive,
  "copia-closed-pr": copiaClosedPr,
  "copia-merged-pr": copiaMergedPr,
  "copia-opened-pr": copiaOpenPr,
  "octicon-x-circle-fill": octiconXCircleFill,
};

const parser = new DOMParser();
const serializer = new XMLSerializer();

// retrieve a HTML string for given SVG icon name, size and additional classes
export function svg(name, size = 16, className = "") {
  if (!(name in svgs)) return "";
  if (size === 16 && !className) return svgs[name];

  const document = parser.parseFromString(svgs[name], "image/svg+xml");
  const svgNode = document.firstChild;
  if (size !== 16) svgNode.setAttribute("width", String(size));
  if (size !== 16) svgNode.setAttribute("height", String(size));
  if (className) svgNode.classList.add(...className.split(/\s+/));
  return serializer.serializeToString(svgNode);
}

function svgParseOuterInner(name) {
  const svgStr = svgs[name];
  if (!svgStr) throw new Error(`Unknown SVG icon: ${name}`);

  // parse the SVG string to 2 parts
  // * svgInnerHtml: the inner part of the SVG, will be used as the content of the <svg> VNode
  // * svgOuter: the outer part of the SVG, including attributes
  // the builtin SVG contents are clean, so it's safe to use `indexOf` to split the content:
  // eg: <svg outer-attributes>${svgInnerHtml}</svg>
  const p1 = svgStr.indexOf(">"),
    p2 = svgStr.lastIndexOf("<");
  if (p1 === -1 || p2 === -1) throw new Error(`Invalid SVG icon: ${name}`);
  const svgInnerHtml = svgStr.slice(p1 + 1, p2);
  const svgOuterHtml = svgStr.slice(0, p1 + 1) + svgStr.slice(p2);
  const svgDoc = parseDom(svgOuterHtml, "image/svg+xml");
  const svgOuter = svgDoc.firstChild;
  return { svgOuter, svgInnerHtml };
}

export const SvgIcon = defineComponent({
  name: "SvgIcon",
  props: {
    name: { type: String, required: true },
    size: { type: Number, default: 16 },
    symbolId: { type: String },
  },
  render() {
    let { svgOuter, svgInnerHtml } = svgParseOuterInner(this.name);
    // https://vuejs.org/guide/extras/render-function.html#creating-vnodes
    // the `^` is used for attr, set SVG attributes like 'width', `aria-hidden`, `viewBox`, etc
    const attrs = {};
    for (const attr of svgOuter.attributes) {
      if (attr.name === "class") continue;
      attrs[`^${attr.name}`] = attr.value;
    }
    attrs[`^width`] = this.size;
    attrs[`^height`] = this.size;

    const classes = Array.from(svgOuter.classList);
    if (this.symbolId) {
      classes.push("tw-hidden", "svg-symbol-container");
      svgInnerHtml = html`<symbol
        id="${this.symbolId}"
        viewBox="${attrs["^viewBox"]}"
        >${htmlRaw(svgInnerHtml)}</symbol
      >`;
    }
    // create VNode
    return h("svg", {
      ...attrs,
      class: classes,
      innerHTML: svgInnerHtml,
    });
  },
});
